import { autorun, observe, intercept } from 'mobx';
import gcLangService from '@greycat/lang-monaco';
import { serialize, deserialize } from 'serializr';
import storage from './storage';
import EditorStore from './stores/EditorStore';
import { WS_FILE, WS_RENAME, WS_ADD, WS_REMOVE, WS_LOAD } from '../common/commands';
export default {
    /**
     * Adds reactions to store to perform specific actions like ls/fs sync & etc.
     * @param store app state
     */
    configure(store) {
        const disposables = [
            // automatically register types & functions on @greycat/lang-monaco when they change
            autorun(() => {
                gcLangService.addTypes(store.globalTypes);
                gcLangService.addFunctions(Object.values(store.globalFunctions));
            }),
            // sync gcLang strict-mode
            autorun(() => {
                gcLangService.setSettings({ lint: store.settings.strictMode });
            }),
            // automatically interpret extensions on workspace changes
            autorun(() => {
                store.sidebar.editorWorkspaces.workspaces.forEach((ws) => {
                    store.interpretExtension(ws);
                });
            }),
            // sync changes with storage
            autorun(() => storage.save(store), { delay: 1000 }),
            // change <body> classList when nightMode is toggled
            autorun(() => {
                if (store.settings.nightMode) {
                    if (!document.body.classList.contains('night')) {
                        document.body.classList.add('night');
                    }
                }
                else {
                    document.body.classList.remove('night');
                }
            }),
            // set at least one workspace as "active" (if there are at least one)
            autorun(() => {
                const { workspaces } = store.sidebar.editorWorkspaces;
                if (workspaces.length > 0 && !workspaces.find((ws) => ws.isActive)) {
                    workspaces[0].isActive = true;
                }
            }),
        ];
        if ('true' === process.env.TARGET_ELECTRON) {
            const { ipcRenderer } = require('electron');
            const interceptors = new Map();
            ipcRenderer.on(WS_LOAD, (_event, workspaces) => {
                // dispose every interceptors when we load another set of workspaces
                interceptors.forEach((dispose) => dispose());
                interceptors.clear();
                // load the new set of workspaces
                store.sidebar.editorWorkspaces.workspaces.replace(workspaces.map((ws) => deserialize(EditorStore, ws)));
            });
            const contentInterceptor = (ws) => {
                const { rootDir } = store.sidebar.editorWorkspaces;
                ws.files.forEach((f) => {
                    const key = `${ws.name}/${f.uri}`;
                    interceptors.set(key, intercept(f, 'content', (i) => {
                        // tslint:disable-next-line:no-console
                        console.log('[workspaces][content]', rootDir, ws.name, f.uri);
                        ipcRenderer.send(WS_FILE, rootDir, ws.name, f.uri, i.newValue);
                        return i;
                    }));
                });
            };
            const renameInterceptor = (ws) => {
                const { rootDir } = store.sidebar.editorWorkspaces;
                interceptors.set(ws.name, intercept(ws, 'name', (i) => {
                    // tslint:disable-next-line:no-console
                    console.log('[workspaces][rename]', rootDir, ws.name, i.newValue);
                    ipcRenderer.send(WS_RENAME, rootDir, ws.name, i.newValue);
                    interceptors.set(i.newValue, interceptors.get(ws.name));
                    interceptors.delete(ws.name);
                    return i;
                }));
            };
            // register interceptors for current workspaces (renames/content)
            store.sidebar.editorWorkspaces.workspaces.forEach((ws) => {
                renameInterceptor(ws);
                contentInterceptor(ws);
            });
            // register add/delete interceptors on the workspaces
            disposables.push(observe(store.sidebar.editorWorkspaces.workspaces, (change) => {
                const rootDir = store.sidebar.editorWorkspaces.rootDir;
                // tslint:disable-next-line:no-console
                console.log('=> observe(workspaces)', change);
                if (change.type === 'splice') {
                    change.added.forEach((ws) => {
                        // tslint:disable-next-line:no-console
                        console.log('[workspaces][add]', rootDir, ws.name);
                        ipcRenderer.send(WS_ADD, rootDir, serialize(ws));
                        // react to renaming for new workspaces
                        renameInterceptor(ws);
                        // react to content updates
                        contentInterceptor(ws);
                    });
                    change.removed.forEach((ws) => {
                        interceptors.forEach((dispose, name) => {
                            if (name.startsWith(ws.name)) {
                                // dispose all interceptors that starts with "ws.name"
                                dispose();
                                if (name === ws.name) {
                                    // tslint:disable-next-line:no-console
                                    console.log('[workspaces][remove]', rootDir, ws.name);
                                    // actually remove the directory on the filesystem
                                    ipcRenderer.send(WS_REMOVE, rootDir, ws.name);
                                }
                            }
                        });
                    });
                }
            }));
        }
        return disposables;
    },
};
